import { useId } from 'react'

const MAJOR = 'var(--doc-grid-major)'
const MINOR = 'var(--doc-grid-minor)'

/**
 * First major grid line in each tile is offset by half a point so SVG strokes stay crisp at 1:1
 * — factor this in when computing a `phaseOffsetX`/`phaseOffsetY` to anchor a major line at a
 * specific canvas position (see `DocumentGridOverlayProps.phaseOffsetX`).
 */
export const DOC_GRID_MAJOR_LINE_OFFSET_PT = 0.5

type DocumentGridOverlayProps = {
  left: number
  top: number
  width: number
  height: number
  /** Full: 48×48pt tile (12pt minors); half: 24×24 (6pt minors) — must match `documentGridSnapStepPt`. */
  mode: 'full' | 'half'
  /**
   * Above print-bleed / inner-trim / spine tints (z≤8) and dashed trim (z≈9) so grid lines stay
   * visible on bleed and trim like Figma; below spine axis / margin chrome (z≥11).
   */
  zIndex?: number
  /**
   * Shifts the pattern's own tile origin (SVG `<pattern x>`), independent of this overlay's
   * `left` — lets a caller anchor a major line to any canvas position (e.g. the spine seam, or
   * a trim edge) without that position needing to fall on this element's own edge. A major line
   * lands at local x ≡ `phaseOffsetX + DOC_GRID_MAJOR_LINE_OFFSET_PT` (mod tile); to anchor a
   * major line at canvas position `anchorX` for an overlay whose own `left` is `left`, pass
   * `phaseOffsetX = anchorX - left - DOC_GRID_MAJOR_LINE_OFFSET_PT`. Defaults to 0 (tile origin
   * at this element's own top-left, matching the previous unconditional behavior).
   */
  phaseOffsetX?: number
  /** Same idea as `phaseOffsetX`, for the vertical axis (SVG `<pattern y>`). */
  phaseOffsetY?: number
}

const lineProps = {
  strokeWidth: 1,
  vectorEffect: 'nonScalingStroke' as const,
  strokeLinecap: 'square' as const,
}

/**
 * Tiled layout grid over a bleed half: SVG `<pattern>` instead of CSS gradients for sharper tiling;
 * `vector-effect: non-scaling-stroke` keeps hairlines consistent under canvas zoom.
 */
export function DocumentGridOverlay({
  left,
  top,
  width,
  height,
  mode,
  zIndex = 10,
  phaseOffsetX = 0,
  phaseOffsetY = 0,
}: DocumentGridOverlayProps) {
  const rawId = useId().replace(/:/g, '')
  const patternId = `doc-grid-pattern-${rawId}`
  const tile = mode === 'full' ? 48 : 24
  const minorXs = mode === 'full' ? [12, 24, 36] : [6, 12, 18]
  const minorYs = minorXs

  if (width <= 0 || height <= 0) {
    return null
  }

  return (
    <svg
      aria-hidden
      className="absolute pointer-events-none overflow-hidden"
      width={width}
      height={height}
      style={{ left, top, zIndex }}
    >
      <defs>
        <pattern
          id={patternId}
          width={tile}
          height={tile}
          x={phaseOffsetX}
          y={phaseOffsetY}
          patternUnits="userSpaceOnUse"
        >
          <g shapeRendering="crispEdges">
            <line
              x1={DOC_GRID_MAJOR_LINE_OFFSET_PT}
              y1={0}
              x2={DOC_GRID_MAJOR_LINE_OFFSET_PT}
              y2={tile}
              stroke={MAJOR}
              {...lineProps}
            />
            {minorXs.map((x) => (
              <line
                key={`v-${x}`}
                x1={x + DOC_GRID_MAJOR_LINE_OFFSET_PT}
                y1={0}
                x2={x + DOC_GRID_MAJOR_LINE_OFFSET_PT}
                y2={tile}
                stroke={MINOR}
                {...lineProps}
              />
            ))}
            <line
              x1={0}
              y1={DOC_GRID_MAJOR_LINE_OFFSET_PT}
              x2={tile}
              y2={DOC_GRID_MAJOR_LINE_OFFSET_PT}
              stroke={MAJOR}
              {...lineProps}
            />
            {minorYs.map((y) => (
              <line
                key={`h-${y}`}
                x1={0}
                y1={y + DOC_GRID_MAJOR_LINE_OFFSET_PT}
                x2={tile}
                y2={y + DOC_GRID_MAJOR_LINE_OFFSET_PT}
                stroke={MINOR}
                {...lineProps}
              />
            ))}
          </g>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${patternId})`} />
    </svg>
  )
}
